import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalHeader,
    Radio,
    Stack,
    Text,
    Textarea,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
    useRadioGroup,
    useToast,
} from "@caspeco/casper-ui-library";
import { useInquiryRespond } from "api/api-hooks/use-inquiry-respond";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { formatInquiryAdjustments } from "components/booking-request/request-modal-helper";

interface AdjustmentDisplayProps {
    type: "time" | "activity";
    original: string;
    proposed: string;
}

const AdjustmentDisplay = ({ type, original, proposed }: AdjustmentDisplayProps) => {
    const { t } = useTranslation();

    const getLabels = () => {
        if (type === "time") {
            return {
                original: t("bookingRequest.respond.labels.desiredTime"),
                proposed: t("bookingRequest.respond.labels.newTime"),
            };
        } else {
            return {
                original: t("bookingRequest.respond.labels.desiredActivity"),
                proposed: t("bookingRequest.respond.labels.proposal"),
            };
        }
    };

    const labels = getLabels();

    return (
        <Stack spacing={ThemeSpaceVariable.XSmall} px={ThemeSpaceVariable.Medium}>
            <Text>
                <Text as="span" fontWeight="bold">
                    {labels.original}
                </Text>{" "}
                {original}
            </Text>
            <Text>
                <Text as="span" fontWeight="bold">
                    {labels.proposed}
                </Text>{" "}
                {proposed}
            </Text>
        </Stack>
    );
};

interface IBookingRequestRespondModal {
    isOpen: boolean;
    onClose: () => void;
}

interface IRespondForm {
    response: "accept" | "decline";
    message: string;
}

const BookingRequestRespondModal = ({ isOpen, onClose }: IBookingRequestRespondModal) => {
    const { t } = useTranslation();
    const { mutate: respondMutate, isPending: respondIsLoading } = useInquiryRespond();
    const { data: booking } = useWebBooking();
    const { showToast } = useToast();

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
        reset,
    } = useForm<IRespondForm>({
        defaultValues: {
            response: "accept",
            message: "",
        },
    });

    const response = watch("response");

    const { getRootProps, getRadioProps } = useRadioGroup({
        name: "response",
        defaultValue: "accept",
    });

    const onSubmit = (data: IRespondForm) => {
        respondMutate(
            {
                accept: data.response === "accept",
                message: data.message,
            },
            {
                onSuccess: () => {
                    reset();
                    onClose();
                },
                onError: () => {
                    showToast({
                        type: "error",
                        title: t("bookingRequest.respond.respondFailedTitle"),
                        description: t("bookingRequest.respond.respondFailedDescription"),
                        duration: 5000,
                    });
                },
            }
        );
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const adjustments = formatInquiryAdjustments(booking?.inquiryValues);
    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md">
            <ModalHeader
                display="flex"
                justifyContent="space-between"
                p={ThemeSpaceVariable.Medium}
            >
                <Text fontWeight="medium" fontSize={ThemeFontSizeVariable.XLarge}>
                    {t("bookingRequest.respond.title")}
                </Text>
                <ModalCloseButton />
            </ModalHeader>
            <ModalBody display="flex" flexDirection="column" p={ThemeSpaceVariable.Medium}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void handleSubmit(onSubmit)();
                    }}
                >
                    <Stack spacing={ThemeSpaceVariable.Medium}>
                        <Text>{t("bookingRequest.respond.intro")}</Text>
                        {adjustments.length > 0 && (
                            <>
                                <Text>{t("bookingRequest.respond.adjustmentIntro")}</Text>
                                <Stack spacing={ThemeSpaceVariable.Medium}>
                                    {adjustments.map((adjustment, index) => (
                                        <AdjustmentDisplay
                                            key={index}
                                            type={adjustment.type}
                                            original={adjustment.original}
                                            proposed={adjustment.proposed}
                                        />
                                    ))}
                                </Stack>
                            </>
                        )}

                        <Text>{t("bookingRequest.respond.confirmMessage")}</Text>
                        <Controller
                            name="response"
                            control={control}
                            render={({ field }) => (
                                <Stack
                                    {...getRootProps()}
                                    spacing={ThemeSpaceVariable.Medium}
                                    direction="row"
                                    flexWrap="wrap"
                                >
                                    <Radio
                                        {...getRadioProps({ value: "accept" })}
                                        isChecked={field.value === "accept"}
                                        onChange={() => field.onChange("accept")}
                                    >
                                        {t("bookingRequest.respond.accept")}
                                    </Radio>
                                    <Radio
                                        {...getRadioProps({ value: "decline" })}
                                        isChecked={field.value === "decline"}
                                        onChange={() => field.onChange("decline")}
                                    >
                                        {t("bookingRequest.respond.decline")}
                                    </Radio>
                                </Stack>
                            )}
                        />
                        <Controller
                            name="message"
                            control={control}
                            rules={{
                                required:
                                    response === "decline"
                                        ? {
                                              value: true,
                                              message: t("bookingRequest.respond.messageRequired"),
                                          }
                                        : false,
                                maxLength: {
                                    value: 255,
                                    message: t("bookingRequest.respond.messageMaxLength"),
                                },
                            }}
                            render={({ field }) => (
                                <FormControl isInvalid={!!errors.message}>
                                    <FormLabel>
                                        {t("bookingRequest.respond.messageLabel")}
                                        {response === "decline" && (
                                            <Text as="span" color={ThemeColorVariable.Error}>
                                                {" *"}
                                            </Text>
                                        )}
                                    </FormLabel>
                                    <Textarea
                                        {...field}
                                        placeholder={
                                            response === "decline"
                                                ? t(
                                                      "bookingRequest.respond.messagePlaceholderDecline"
                                                  )
                                                : t("bookingRequest.respond.messagePlaceholder")
                                        }
                                        rows={4}
                                        isInvalid={!!errors.message}
                                        onChange={(e) => {
                                            if (e.target.value.length > 255) return;
                                            field.onChange(e.target.value);
                                        }}
                                    />
                                    <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
                                </FormControl>
                            )}
                        />

                        <Stack
                            direction="row"
                            spacing={ThemeSpaceVariable.Medium}
                            justifyContent="flex-end"
                        >
                            <Button
                                variant="ghost"
                                onClick={handleClose}
                                disabled={respondIsLoading}
                                size="xl"
                            >
                                {t("bookingRequest.respond.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                isLoading={respondIsLoading}
                                loadingText={t("bookingRequest.respond.sending")}
                                size="xl"
                            >
                                {t("bookingRequest.respond.send")}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </ModalBody>
        </Modal>
    );
};

export default BookingRequestRespondModal;
